import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { StoreInfo, getStoreList } from "../../services/rentalStockServices";

import StickyHeader from "../../components/Layout/StickyHeader";
import HorizontalRule from "../../components/Rule/Horizontal";
import { useUserContext } from "../../contexts/userContext";
import CommonSelect from "../../components/Select";

import { TalkFindBuildingDownArrow } from "../../pages/Talk";

type Props = {
  title?: string;
  onClickRefresh?: () => void;
};

export default function useStoreHeader({
  title,
  onClickRefresh,
}: Props): [
  { storeId: string | undefined; isLoading: boolean; isEmpty: boolean },
  React.ReactNode
] {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [user] = useUserContext();
  const [store, setStore] = useState<StoreInfo>();
  const [storeList, setStoreList] = useState<any[]>([]);

  const loadStoreList = async () => {
    setIsLoading(true);
    try {
      const result = await getStoreList();
      if (result.status === HttpStatusCode.Ok) {
        setStoreList(result.data.items);
      }
    } catch (e) {
      console.log("", e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOnclickRefresh = async () => {
    await loadStoreList();
    if (onClickRefresh) onClickRefresh();
  };

  useEffect(() => {
    if (user.buildingId) loadStoreList();
  }, [user.buildingId, store]);

  return [
    {
      storeId: store?.storeId,
      isLoading,
      isEmpty:
        storeList === null ||
        storeList === undefined ||
        storeList?.length === 0,
    },
    <>
      <div className="px-5">
        <StickyHeader
          title={title ?? "재고 관리"}
          onBackClick={() => navigate("/manager/rental-manager")}
          rightContent={
            <div onClick={handleOnclickRefresh}>
              <RefreshIcon />
            </div>
          }
        />
      </div>

      <div className="px-5 pt-5 pb-10">
        <div className="flex flex-row items-center gap-5" tabIndex={-1}>
          <CommonSelect
            defaultValue={"전체 대여 장소"}
            arrowIcon={<TalkFindBuildingDownArrow />}
            value={store?.storeId ?? ""}
            unUsePadding
            useTruncate
            optionList={
              [
                { value: "", label: "전체 대여 장소" },
                ...(storeList?.map((store) => ({
                  value: store.storeId,
                  label: store.storeName,
                })) ?? []),
              ] ?? []
            }
            onChange={(target) => {
              const selectedStore = storeList.find(
                (store) => store.storeId === target.value
              );
              setStore(selectedStore);
            }}
            className="text-3xl font-medium leading-[38px] tracking-[-1.2px]"
            optionOrder="between"
            optionWidth="240px"
            wrapperClassName="w-fit max-w-[calc(100vw-40px)]"
            variant="selectBuilding"
          />
        </div>
      </div>
      <HorizontalRule margin="0" className="bg-brand-neutral-05 h-[6px]" />
    </>,
  ];
}

export const RefreshIcon = () => (
  <svg
    data-testid="refresh-icon"
    width="22"
    height="19"
    viewBox="0 0 22 19"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1.85331 6.27197L3.50015 9.12438C3.52542 9.16814 3.58137 9.18313 3.62513 9.15787L6.47754 7.51103"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
    />
    <path
      d="M3.68262 9.17445C3.79316 6.44756 5.25602 3.83469 7.79433 2.3692C11.7327 0.0953859 16.7686 1.44477 19.0425 5.38312C21.3163 9.32148 19.9669 14.3574 16.0285 16.6312C13.3437 18.1813 10.1487 18.0476 7.66652 16.5577C6.50764 15.8621 5.50412 14.8708 4.78041 13.6173"
      stroke="black"
      strokeWidth="1.75"
    />
  </svg>
);
